/**
 * Email Service with EmailJS fallback
 * Handles form submission via Netlify Forms with EmailJS backup
 */

import emailjs from '@emailjs/browser';

export class EmailService {
  constructor() {
    this.emailjsInitialized = false;
    this.emailjsConfig = {
      serviceId: import.meta.env.VITE_EMAILJS_SERVICE_ID,
      templateId: import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
      publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY
    };
  }

  /**
   * Initialize EmailJS
   */
  initEmailJS() {
    if (this.emailjsInitialized) return;

    const { publicKey } = this.emailjsConfig;

    if (!publicKey || publicKey === 'YOUR_PUBLIC_KEY') {
      console.warn('[EmailService] EmailJS not configured. Fallback disabled.');
      return;
    }

    try {
      emailjs.init(publicKey);
      this.emailjsInitialized = true;
      console.log('[EmailService] EmailJS initialized successfully');
    } catch (error) {
      console.error('[EmailService] Failed to initialize EmailJS:', error);
    }
  }

  /**
   * Send form via Netlify Forms (primary method)
   * @param {FormData} formData - Form data to send
   * @returns {Promise<Response>}
   */
  async sendViaNetlify(formData) {
    console.log('[EmailService] Attempting to send via Netlify Forms...');

    const params = new URLSearchParams();
    params.append('form-name', 'contact');

    for (const [key, value] of formData.entries()) {
      if (key !== 'bot-field') {
        params.append(key, value);
      }
    }

    return fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params
    });
  }

  /**
   * Send form via EmailJS (fallback method)
   * @param {Object} formData - Form data object
   * @returns {Promise<Object>}
   */
  async sendViaEmailJS(formData) {
    if (!this.emailjsInitialized) {
      this.initEmailJS();
    }

    if (!this.emailjsInitialized) {
      throw new Error('EmailJS not initialized');
    }

    console.log('[EmailService] Attempting to send via EmailJS...');

    const { serviceId, templateId } = this.emailjsConfig;

    if (!serviceId || !templateId) {
      throw new Error('EmailJS service or template ID not configured');
    }

    // EmailJS expects template parameters
    const templateParams = {
      from_name: formData.name || formData.get('name'),
      from_email: formData.email || formData.get('email'),
      subject: formData.subject || formData.get('subject'),
      message: formData.message || formData.get('message'),
      to_name: 'Delanoe Pirard' // Your name
    };

    return emailjs.send(serviceId, templateId, templateParams);
  }

  /**
   * Send form with automatic fallback and retry
   * Tries Netlify first (with retry), falls back to EmailJS on failure
   * @param {FormData|Object} formData - Form data
   * @param {Function} onProgress - Progress callback (0-100)
   * @returns {Promise<{success: boolean, method: string, error?: Error}>}
   */
  async sendWithFallback(formData, onProgress = null) {
    const maxRetries = 2;
    const retryDelay = 1000; // 1 second

    // Progress tracking
    const updateProgress = (percent) => {
      if (onProgress) onProgress(percent);
    };

    updateProgress(10);

    // Try Netlify Forms first (with retry)
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        updateProgress(10 + (attempt * 20));

        const response = await this.sendViaNetlify(formData);

        if (response.ok || response.status === 200) {
          console.log('[EmailService] ✓ Sent successfully via Netlify Forms');
          updateProgress(100);
          return {
            success: true,
            method: 'netlify',
            response,
            attempts: attempt
          };
        }

        throw new Error(`Netlify Forms failed with status ${response.status}`);
      } catch (error) {
        console.warn(`[EmailService] Netlify attempt ${attempt}/${maxRetries} failed:`, error.message);

        if (attempt < maxRetries) {
          await this.delay(retryDelay);
        }
      }
    }

    updateProgress(60);

    // Try EmailJS as fallback (with retry)
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        updateProgress(60 + (attempt * 15));

        const convertedData = this.convertFormData(formData);
        const response = await this.sendViaEmailJS(convertedData);

        console.log('[EmailService] ✓ Sent successfully via EmailJS (fallback)');
        updateProgress(100);
        return {
          success: true,
          method: 'emailjs',
          response,
          attempts: attempt
        };
      } catch (error) {
        console.warn(`[EmailService] EmailJS attempt ${attempt}/${maxRetries} failed:`, error.message);

        if (attempt < maxRetries) {
          await this.delay(retryDelay);
        }
      }
    }

    console.error('[EmailService] All sending methods failed');
    updateProgress(0);

    return {
      success: false,
      method: 'none',
      error: new Error('All sending methods failed after retries')
    };
  }

  /**
   * Delay helper for retry logic
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Convert FormData to plain object
   * @param {FormData|Object} formData
   * @returns {Object}
   */
  convertFormData(formData) {
    if (!(formData instanceof FormData)) {
      return formData;
    }

    const obj = {};
    for (const [key, value] of formData.entries()) {
      obj[key] = value;
    }
    return obj;
  }

  /**
   * Check if EmailJS is available as fallback
   * @returns {boolean}
   */
  isEmailJSAvailable() {
    const { serviceId, templateId, publicKey } = this.emailjsConfig;
    return !!(serviceId && templateId && publicKey &&
              serviceId !== 'YOUR_SERVICE_ID' &&
              templateId !== 'YOUR_TEMPLATE_ID' &&
              publicKey !== 'YOUR_PUBLIC_KEY');
  }
}

// Export singleton
export const emailService = new EmailService();
